import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Attendee, CheckoutData, Country } from '../../../domain/registrator';
import { CaptchaService } from '../../../services/captcha.service';
import { CheckoutService } from '../../../services/checkout.service';
import { CountryService } from '../../../services/country.service';
import { OptionGroupComponent } from '../../misc/option-group/option-group.component';
import { AttendeeForm, PreferencesForm, RegistrationForm } from '../registration/registration.component';
import { RegistrationStore } from '../../../guards/registration-store';
import { MessageService } from '../../../services/message.service';

@Component({
  selector: 'app-free-registration',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatOptionModule,
    MatRadioModule,
    MatSelectModule,
    MatTableModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    OptionGroupComponent,
    MatSnackBarModule
  ],
  templateUrl: './free-registration.component.html',
  styleUrl: './free-registration.component.scss',
})
export class FreeRegistrationComponent {
  private _snackBar = inject(MatSnackBar);
  private readonly formBuilder = inject(FormBuilder);
  private readonly checkoutService = inject(CheckoutService);
  private readonly countryService = inject(CountryService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly captchaService = inject(CaptchaService);
  event = this.route.parent!.snapshot.data['event'];
  private readonly registrationStore = inject(RegistrationStore);

  loading = signal<boolean>(false);

  countries: Country[] = [];
  attendeesColumnsToDisplay = ['firstName', 'lastName', 'jobTitle', 'email'];
  captchaToken: string | null = null;
  // honeypot: real users never fill this hidden field
  hpField = '';
  formLoadedAt = 0;
  registrationFormGroup!: FormGroup<RegistrationForm>;

  constructor() {
    this.buildRegistrationFormGroup();
  }

  ngOnInit() {
    this.formLoadedAt = Date.now();

    this.countryService.countries.subscribe((x) => {
      this.countries = x;
    });
  }

  private buildRegistrationFormGroup(): void {
    this.registrationFormGroup = this.formBuilder.group<RegistrationForm>({
      organisation: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
      selectedFee: this.formBuilder.control(null),
      firstAttendee: this.buildAttendeeFormGroup(true),
      additionalAttendees: this.formBuilder.array<FormGroup<AttendeeForm>>([])
    }, {
      updateOn: 'blur'
    });
  }

  private buildAttendeeFormGroup(corresponding: boolean): FormGroup<AttendeeForm> {
    const preferenceControls: PreferencesForm = {};

    for (const group of this.event.optionGroups) {
      preferenceControls[group.id] = this.formBuilder.control<number[]>([], {
        nonNullable: true
      });
    }

    const fg = this.formBuilder.group<AttendeeForm>({
      jobTitle: this.formBuilder.control('', { validators: [Validators.required], nonNullable: true }),
      firstName: this.formBuilder.control('', { validators: [Validators.required], nonNullable: true }),
      lastName: this.formBuilder.control('', { validators: [Validators.required], nonNullable: true }),
      email: this.formBuilder.control('', { validators: [Validators.required, Validators.email], nonNullable: true }),
      preferences: this.formBuilder.group(preferenceControls as PreferencesForm, { updateOn: 'blur' }),
      corresponding: this.formBuilder.control(corresponding, { nonNullable: true }),
    }, {
      updateOn: 'blur'
    });
    return fg;
  }

  async register() {
    console.debug('Register', this.registrationFormGroup.valid, this.registrationFormGroup);
    this.loading.set(true);

    this.registrationFormGroup.markAllAsTouched();
    if (this.registrationFormGroup.valid) {
      console.log(this.registrationFormGroup.getRawValue());
      // solve the proof-of-work captcha at submit time, so the token is fresh
      try {
        this.captchaToken = await this.captchaService.execute('submit');
      } catch (e) {
        this.loading.set(false);
        console.error('captcha failed', e);
        this.messageService.showMessage('Captcha verification failed; please try again');
        return;
      }
      // clear any browser-autofilled honeypot value; real users run JS, so the
      // field is empty for them — only non-JS bots leave it filled
      this.hpField = '';
      const request: CheckoutData = {
        eventShortName: this.event.shortName,
        registrationData: this.registrationFormGroup.getRawValue(),
        invoicingData: null,
        totalTickets: 0,
        payingTickets: 0,
        freeTickets: 0,
        fullRateTickets: 0,
        discountRateTickets: 0,
        fullRate: 0,
        discountRate: 0,
        totalAmount: 0,
        token: this.captchaToken,
        hpField: this.hpField,
        formLoadedAt: this.formLoadedAt,
        paymentMode: null,
        successUrl: null,
        cancelUrl: null,
        pendingUrl: null
      };

      this.checkoutService.checkout(request).subscribe({
        next: v => {
          this.loading.set(false);
          console.debug('Success', v)
          this.registrationStore.markCompleted();
          this.router.navigate(['confirm'], {
            relativeTo: this.route.parent
          });
        },
        error: err => {
          this.loading.set(false);
          const message = err.error?.errorMessage || 'Unexpected error occurred';
          console.log(err.status, err, message);
          this.messageService.showMessage(message);
        }
      })
    } else {
      this.loading.set(false);
      this._snackBar.open('There is missing or invalid data; please double check', 'Dismiss', { duration: 3000 });
    }
  }

  addAttendee() {
    this.additionalAttendeesFormArray.push(this.buildAttendeeFormGroup(false), { emitEvent: false });
  }

  deleteAttendee(i: number) {
    console.log("deleting " + i);
    this.additionalAttendeesFormArray.removeAt(i);
  }

  get additionalAttendeesFormArray() {
    return this.registrationFormGroup.controls["additionalAttendees"] as FormArray<FormGroup<AttendeeForm>>;
  }

  get allAttendees(): Attendee[] {
    let allAttendees: Attendee[] = [];

    const firstAttendeeGroup = this.registrationFormGroup.get('firstAttendee') as FormGroup;
    const firstAttendee: Attendee = firstAttendeeGroup.value;
    const attendeesArray = this.registrationFormGroup.get('additionalAttendees') as FormArray;
    const additionalAttendees: Attendee[] = attendeesArray.value;

    if ((firstAttendeeGroup.valid) && (attendeesArray.valid)) {
      allAttendees = [firstAttendee, ...additionalAttendees];
    }
    return allAttendees;
  }
}
